import { getPreviewFormatFromUrl } from "./preview";
import {
  formatCoordinateForSvg,
  hasPositionCoordinates,
} from "./variables";

const PLACEHOLDER_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

function escapeSvgText(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function formatDateDefault(value, format) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  if (format === "DD/MM/YYYY") {
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  return date.toISOString().slice(0, 10);
}

export function formatVariableDefaultForSvg(attrs = {}) {
  const safeAttrs = attrs && typeof attrs === "object" ? attrs : {};
  const dataType = String(safeAttrs["data-type"] || "").toLowerCase();
  const defaultValue = safeAttrs["data-default-value"];
  const label = safeAttrs["data-label"] || safeAttrs["data-key"] || "";

  if (defaultValue === undefined || defaultValue === null || defaultValue === "") {
    return label;
  }

  if (dataType === "date") {
    return formatDateDefault(defaultValue, safeAttrs["data-format"]);
  }

  if (dataType === "file" || defaultValue === "base64 string") {
    return label ? `[${label}]` : "[file]";
  }

  if (dataType === "qr-code") {
    return label || "[QR]";
  }

  return String(defaultValue);
}

export function buildSvgVariableReplacementMap(variableConfig = {}) {
  const map = {};

  Object.entries(variableConfig).forEach(([variableKey, attrs]) => {
    if (!attrs || typeof attrs !== "object") {
      return;
    }

    const formatted = formatVariableDefaultForSvg(attrs);
    map[variableKey] = formatted;

    const dataKey = attrs["data-key"];
    if (dataKey && dataKey !== variableKey) {
      map[dataKey] = formatted;
    }

    const dataName = attrs["data-name"];
    if (dataName && !map[dataName]) {
      map[dataName] = formatted;
    }
  });

  return map;
}

export function substituteSvgTemplateVariables(svgText, variableConfig = {}) {
  if (!svgText || typeof svgText !== "string") {
    return { svg: "", unmatched: [], replacedCount: 0 };
  }

  const replacementMap = buildSvgVariableReplacementMap(variableConfig);
  const unmatched = new Set();
  let replacedCount = 0;

  const svg = svgText.replace(PLACEHOLDER_PATTERN, (match, rawKey) => {
    const key = rawKey.trim();
    if (!Object.prototype.hasOwnProperty.call(replacementMap, key)) {
      unmatched.add(key);
      return match;
    }

    replacedCount += 1;
    return escapeSvgText(replacementMap[key]);
  });

  return {
    svg,
    unmatched: Array.from(unmatched),
    replacedCount,
  };
}

export function isSvgTemplateUrl(url) {
  return getPreviewFormatFromUrl(url) === "svg";
}

function collectVariableLookupKeys(variableKey, attrs = {}) {
  const keys = new Set();
  if (variableKey) keys.add(String(variableKey));
  if (attrs["data-name"]) keys.add(String(attrs["data-name"]));
  if (attrs["data-key"]) keys.add(String(attrs["data-key"]));
  return Array.from(keys);
}

function elementContainsPlaceholder(element, placeholder) {
  return String(element?.textContent || "").includes(placeholder);
}

function findSvgElementsForVariable(svgDoc, variableKey, attrs = {}) {
  const elements = new Set();
  const lookupKeys = collectVariableLookupKeys(variableKey, attrs);

  lookupKeys.forEach((key) => {
    const placeholder = `{{${key}}}`;

    svgDoc
      .querySelectorAll(`[data-name="${key}"]`)
      .forEach((element) => elements.add(element));
    svgDoc
      .querySelectorAll(`[data-key="${key}"]`)
      .forEach((element) => elements.add(element));
    svgDoc
      .querySelectorAll(`[id="${placeholder}"]`)
      .forEach((element) => elements.add(element));

    svgDoc.querySelectorAll("text, tspan").forEach((element) => {
      if (elementContainsPlaceholder(element, placeholder)) {
        elements.add(element);
      }
    });
  });

  return Array.from(elements);
}

function getCoordinateTargets(element) {
  const tagName = element.tagName?.toLowerCase();

  if (tagName === "text") {
    const tspans = element.querySelectorAll(":scope > tspan");
    if (tspans.length > 0) {
      return Array.from(tspans);
    }
  }

  return [element];
}

function getTextAnchorTarget(element) {
  const tagName = element.tagName?.toLowerCase();

  if (tagName === "text") {
    return element;
  }

  return element.closest("text") || element;
}

function applyPositionAttributes(element, attrs) {
  if (!hasPositionCoordinates(attrs) && !attrs["text-anchor"]) {
    return;
  }

  if (hasPositionCoordinates(attrs)) {
    getCoordinateTargets(element).forEach((target) => {
      target.setAttribute("x", formatCoordinateForSvg(attrs.x));
      target.setAttribute("y", formatCoordinateForSvg(attrs.y));
    });
  }

  const textAnchor = attrs["text-anchor"];
  if (textAnchor) {
    getTextAnchorTarget(element).setAttribute(
      "text-anchor",
      String(textAnchor),
    );
  }
}

export function applyStableSvgVariableElementIds(svgText) {
  if (!svgText || typeof svgText !== "string") {
    return svgText || "";
  }

  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
    const parseError = svgDoc.querySelector("parsererror");

    if (parseError) {
      return svgText;
    }

    const assignedIds = new Set();

    svgDoc.querySelectorAll("[id]").forEach((element) => {
      const existingId = element.getAttribute("id");
      if (existingId) {
        assignedIds.add(existingId);
      }
    });

    svgDoc
      .querySelectorAll(
        "tspan[data-name], tspan[data-key], text[data-name], text[data-key]",
      )
      .forEach((element) => {
        const key =
          element.getAttribute("data-name") || element.getAttribute("data-key");
        if (!key || element.getAttribute("id")) {
          return;
        }

        const baseId = `template-preview-tspan-${key}`;
        let elementId = baseId;
        let occurrence = 1;

        while (assignedIds.has(elementId)) {
          occurrence += 1;
          elementId = `${baseId}-${occurrence}`;
        }

        element.setAttribute("id", elementId);
        assignedIds.add(elementId);
      });

    return new XMLSerializer().serializeToString(svgDoc);
  } catch {
    return svgText;
  }
}

export function applyVariableCoordinatesToSvg(svgText, variableConfig = {}) {
  if (!svgText || typeof svgText !== "string") {
    return svgText || "";
  }

  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
    const parseError = svgDoc.querySelector("parsererror");

    if (parseError) {
      return svgText;
    }

    Object.entries(variableConfig).forEach(([variableKey, attrs]) => {
      const safeAttrs = attrs && typeof attrs === "object" ? attrs : {};
      if (!hasPositionCoordinates(safeAttrs) && !safeAttrs["text-anchor"]) {
        return;
      }

      const elements = findSvgElementsForVariable(svgDoc, variableKey, safeAttrs);
      elements.forEach((element) => applyPositionAttributes(element, safeAttrs));
    });

    return new XMLSerializer().serializeToString(svgDoc);
  } catch {
    return svgText;
  }
}

export function processSvgTemplate(
  svgText,
  variableConfig = {},
  { fillDefaults = false } = {},
) {
  const positionedSvg = applyStableSvgVariableElementIds(
    applyVariableCoordinatesToSvg(svgText, variableConfig),
  );

  if (!fillDefaults) {
    return {
      svg: positionedSvg,
      unmatched: [],
      replacedCount: 0,
    };
  }

  return substituteSvgTemplateVariables(positionedSvg, variableConfig);
}

export async function fetchAndProcessSvgTemplate(
  url,
  variableConfig,
  { fillDefaults = false } = {},
) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch SVG template (${response.status})`);
  }

  const svgText = await response.text();
  return processSvgTemplate(svgText, variableConfig, { fillDefaults });
}

export async function fetchAndSubstituteSvgTemplate(url, variableConfig) {
  return fetchAndProcessSvgTemplate(url, variableConfig, { fillDefaults: true });
}
